/**
 * Body weight: record it, and watch it over time.
 *
 * Reads and writes Dexie only — no endpoint, no awaiting. Stepping on the scale
 * in a gym with no signal has to work exactly like logging a set does, and the
 * chart has to be there when you look at it. The outbox carries the weigh-in to
 * the server whenever there happens to be a connection.
 *
 * Storage is kilograms, always. The field is typed in whatever `unit_pref` says
 * and converted once on the way in, same rule as every weight in the app.
 *
 * One point per local day, not per row. Weighing yourself twice on a Tuesday is
 * two facts about Tuesday, not two days of progress, and the chart's x-axis
 * spaces points evenly — so a same-day pair would draw a spike that means
 * nothing. The last weigh-in of a day wins.
 */

import { useMemo, useState } from 'react'
import { useLiveQuery } from 'dexie-react-hooks'

import type { Metric } from '../db'
import { weighIns } from '../db/queries'
import { addMetric, deleteMetric } from '../db/mutations'
import { syncSoon } from '../sync/engine'
import { ProgressChart, type Point } from './ProgressChart'
import { displayToKg, formatWeight, kgToDisplay, type Unit } from '../lib/units'
import { dayIso, dayOf, todayIso } from '../lib/day'
import { parseIso } from '../lib/time'

/** How far back the "since" comparison looks for something to compare against. */
const TREND_DAYS = 30

/** Sanity bounds on the typed value, in the unit being typed. Rejects a
 *  mistyped 1750 and a stray 7, both of which would wreck the chart's scale
 *  and — worse — the bodyweight ratios the planner reasons from. */
const LIMITS: Record<Unit, { min: number; max: number }> = {
  kg: { min: 20, max: 400 },
  lb: { min: 45, max: 880 },
}

export function BodyWeightCard({ unit }: { unit: Unit }) {
  const rows = useLiveQuery(weighIns, [], undefined as Metric[] | undefined)
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState('')
  const [date, setDate] = useState(todayIso())
  const [error, setError] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)

  // One entry per local day, last wins. `rows` arrives oldest-first, so a plain
  // overwrite into a Map keyed by day leaves the latest of each.
  const byDay = useMemo(() => {
    const map = new Map<string, Metric>()
    for (const r of rows ?? []) map.set(dayOf(r.measured_at), r)
    return [...map.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1))
  }, [rows])

  const points: Point[] = useMemo(
    () => byDay.map(([day, m]) => ({ date: day, value: m.weight_kg ?? null })),
    [byDay],
  )

  const latest = byDay.length ? byDay[byDay.length - 1][1] : null
  const trend = useMemo(() => describeTrend(byDay, unit), [byDay, unit])

  const save = async () => {
    const typed = parseFloat(value.replace(',', '.'))
    if (!Number.isFinite(typed)) {
      setError('Type a number.')
      return
    }
    const { min, max } = LIMITS[unit]
    if (typed < min || typed > max) {
      setError(`That should be between ${min} and ${max} ${unit}.`)
      return
    }
    const kg = displayToKg(typed, unit)
    if (kg === null) {
      setError('Type a number.')
      return
    }

    await addMetric({ weight_kg: kg, measured_at: measuredAtFor(date) })
    syncSoon()
    setValue('')
    setDate(todayIso())
    setError(null)
    setOpen(false)
  }

  // undefined = Dexie hasn't answered yet. Distinct from "no weigh-ins", which
  // is an empty array and gets the invitation below rather than a blank card.
  if (rows === undefined) return null

  const listed = showAll ? [...byDay].reverse() : [...byDay].reverse().slice(0, 5)

  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <div className="row" style={{ marginBottom: latest ? '.2rem' : '.5rem' }}>
        <strong style={{ fontSize: '.95rem' }}>Body weight</strong>
        {latest && (
          <span className="muted" style={{ fontSize: '.82rem', textAlign: 'right' }}>
            {trend}
          </span>
        )}
      </div>

      {latest ? (
        <>
          <div style={{ fontSize: '1.6rem', fontWeight: 600, lineHeight: 1.1 }}>
            {formatWeight(latest.weight_kg, unit)}
          </div>
          <div className="muted" style={{ fontSize: '.8rem', marginBottom: '.6rem' }}>
            {relativeDay(latest.measured_at)}
          </div>
        </>
      ) : (
        <p className="muted" style={{ margin: '0 0 .6rem', fontSize: '.88rem' }}>
          Record your weight and it charts itself from here. Plans use it too —
          a pull up is your whole body weight every rep, so knowing it is what
          lets the planner tell the difference between an exercise you can train
          and one you can only fail at.
        </p>
      )}

      {points.length >= 2 && (
        <ProgressChart
          title="Weigh-ins"
          points={points}
          unit={unit}
          tone="secondary"
          height={130}
          format={(v) => formatWeight(v, unit, { withUnit: false })}
        />
      )}

      {open ? (
        <div style={{ marginTop: '.5rem' }}>
          <label htmlFor="bw-value">Weight ({unit})</label>
          <input
            id="bw-value"
            type="number"
            inputMode="decimal"
            step="0.1"
            autoFocus
            value={value}
            onChange={(e) => { setValue(e.target.value); setError(null) }}
            onKeyDown={(e) => { if (e.key === 'Enter') void save() }}
          />

          <label htmlFor="bw-date">Date</label>
          <input
            id="bw-date"
            type="date"
            value={date}
            max={todayIso()}
            onChange={(e) => setDate(e.target.value)}
          />

          {error && <div className="error">{error}</div>}

          <div style={{ display: 'flex', gap: '.5rem' }}>
            <button
              className="secondary"
              style={{ flex: 1 }}
              onClick={() => { setOpen(false); setValue(''); setError(null) }}
            >
              Cancel
            </button>
            <button style={{ flex: 2 }} disabled={!value.trim()} onClick={() => void save()}>
              Save
            </button>
          </div>
        </div>
      ) : (
        <button
          className="secondary"
          style={{ width: '100%', marginTop: points.length >= 2 ? 0 : '.25rem' }}
          onClick={() => setOpen(true)}
        >
          {latest ? 'Log a weigh-in' : 'Log your weight'}
        </button>
      )}

      {listed.length > 0 && (
        <>
          <ul className="plain" style={{ marginTop: '.75rem', marginBottom: 0 }}>
            {listed.map(([day, m]) => (
              <li key={m.id}>
                <span>{formatWeight(m.weight_kg, unit)}</span>
                <span className="muted" style={{ fontSize: '.82rem' }}>
                  {parseIso(m.measured_at).toLocaleDateString(
                    [], { month: 'short', day: 'numeric', year: '2-digit' })}
                  <button
                    className="secondary"
                    aria-label={`Remove the weigh-in from ${day}`}
                    style={{
                      marginTop: 0, marginLeft: '.5rem', padding: '.1rem .45rem',
                      minHeight: 0, fontSize: '.8rem',
                    }}
                    onClick={() => { void deleteMetric(m.id); syncSoon() }}
                  >
                    ×
                  </button>
                </span>
              </li>
            ))}
          </ul>
          {byDay.length > 5 && (
            <button
              className="secondary"
              style={{ width: '100%', marginTop: '.5rem' }}
              onClick={() => setShowAll((s) => !s)}
            >
              {showAll ? 'Show fewer' : `Show all ${byDay.length}`}
            </button>
          )}
        </>
      )}
    </div>
  )
}

/**
 * "−1.4 lb in 30 days", or an honest admission that there's nothing to compare.
 *
 * Compared against the weigh-in nearest 30 days ago rather than the previous
 * one: day-to-day body weight swings by a couple of pounds on water alone, so
 * "since yesterday" is noise dressed up as a trend.
 */
function describeTrend(byDay: [string, Metric][], unit: Unit): string {
  if (byDay.length < 2) return 'one weigh-in so far'

  const latest = byDay[byDay.length - 1]
  const cutoff = new Date()
  cutoff.setDate(cutoff.getDate() - TREND_DAYS)
  const cutoffIso = dayIso(cutoff)

  // The last entry at or before the cutoff; failing that the oldest one there
  // is, with the window named honestly rather than claimed as 30 days.
  const earlier = [...byDay].reverse().find(([day]) => day <= cutoffIso) ?? byDay[0]
  if (earlier[0] === latest[0]) return 'one weigh-in so far'

  const from = earlier[1].weight_kg ?? 0
  const to = latest[1].weight_kg ?? 0
  const days = Math.max(1, Math.round(
    (parseIso(latest[1].measured_at).getTime() - parseIso(earlier[1].measured_at).getTime())
    / 86400000))

  const deltaKg = to - from
  const shown = kgToDisplay(Math.abs(deltaKg), unit) ?? 0
  const rounded = Math.round(shown * 10) / 10
  if (rounded === 0) return `no change in ${days} day${days === 1 ? '' : 's'}`
  // Minus sign, not a hyphen — this sits next to a number in a sentence.
  const sign = deltaKg > 0 ? '+' : '−'
  return `${sign}${rounded} ${unit} in ${days} day${days === 1 ? '' : 's'}`
}

/** A weigh-in dated today keeps the current clock time; a backdated one lands
 *  at the same time of day on that date. Never `new Date(day)`, which parses a
 *  bare YYYY-MM-DD as UTC midnight and lands on the day before west of
 *  Greenwich — the mistake day.ts exists to stop. */
function measuredAtFor(day: string): string {
  if (day === todayIso()) return new Date().toISOString()
  const now = new Date()
  const [y, m, d] = day.split('-').map(Number)
  return new Date(y, m - 1, d, now.getHours(), now.getMinutes(), 0, 0).toISOString()
}

function relativeDay(iso: string): string {
  const day = dayOf(iso)
  const today = todayIso()
  if (day === today) return 'today'
  // Both sides parse to local noon, so the difference is whole days and a DST
  // shift can't round it to the wrong one.
  const diff = Math.round(
    (parseIso(today).getTime() - parseIso(day).getTime()) / 86400000)
  if (diff === 1) return 'yesterday'
  if (diff < 7) return `${diff} days ago`
  return parseIso(iso).toLocaleDateString([], { month: 'short', day: 'numeric' })
}
